/*
 * Copyright (c) Outright Mental. (https://outrightmental.com) All Rights Reserved.
 */

// app
import {produce} from "immer";
import {toastr} from "react-redux-toastr";
import {GAME_LOSS, GAME_NEW, GAME_QUIT, GAME_START, GAME_TICK, GAME_VICTORY} from "./GameEngine";
import {createSelector} from "reselect";
import {
  DEPARTMENT_CLOSE,
  DEPARTMENT_CLOSING_DONE,
  DEPARTMENT_OPEN,
  DEPARTMENT_OPENING_DONE,
  DEPARTMENT_STATE_OPENED,
  DEPARTMENT_STATE_OPENING,
  DEPARTMENT_TYPE_OPS,
  DEPARTMENTS,
  GAME_CYCLE_EXPENSE_DAYS,
  GAME_CYCLE_MARKET_DAYS,
  GAME_CYCLE_PAYROLL_DAYS,
  GAME_DAYS_PER_SECOND,
  MONEY,
  PERSON_HIRE,
  PERSON_HIRING_DONE,
  PERSON_TERMINATE,
  PERSON_TERMINATION_DONE,
  PLAYER_MESSAGE_TYPE_EXPENSE,
  PLAYER_MESSAGE_TYPE_MARKET,
  PLAYER_MESSAGE_TYPE_PAYROLL
} from "../constants";
import {nextUniqueId} from "../util";

// Setup the initial state
/**
 @typedef {*} BoardState
 */
const initialState = {
  fromMillisUTC: null,
  toMillisUTC: null,
  daysElapsed: 0,
  cycle: {
    payroll: {
      lastDay: 0,
      total: 0,
    },
    expense: {
      lastDay: 0,
      total: 0,
    },
    market: {
      lastDay: 0,
      total: 0,
    },
  },
  uncapturedMarketShare: 100, // percent
  // Only one player for one
  // The BoardEngine contains the data for a single match to determine victory (reset between matches)
  player: [
    {
      messages: [],
      resources: {
        salesValue: 0, // units
        marketShare: 0, // percent
        cash: 1000000, // dollars
      },
      departmentMap: {
        "97bc5289-eb01-44d8-a8fd-8a91ca011808": {
          name: "Headquarters",
          type: DEPARTMENT_TYPE_OPS,
          state: DEPARTMENT_STATE_OPENED,
          openAtDay: 0,
          closeAtDay: 0,
        }
      },
      departmentIdsByType: {
        // automatically computed when selecting this data-- don't write this data
      },
      personMap: {},
    }
  ]
};

/**
 * Mutate the state by sending a message for player N
 * @param state to mutate
 * @param playerId of player
 * @param type
 * @param content
 */
function mutPlayerSendMessage(state, playerId, type, content) {
  state.player[playerId].messages.unshift({type, content});
}

/**
 * The Redux reducer of a board state to an updated board state
 * @param input to update
 * @param {{
 *     type:string,
 *     departmentType:string,
 *     departmentId:string,
 * }} action to reduce
 * @return {*} updated state
 */
export const boardReducer = function (input, action) {

  // use mutate the requested key+value
  switch (action.type) {

    case GAME_LOSS:
      return produce(input, state => {
        state.toMillisUTC = new Date().getTime();
      });

    case GAME_NEW:
      return initialState;

    case GAME_QUIT:
      return produce(input, state => {
        state.toMillisUTC = new Date().getTime();
      });

    case GAME_START:
      return produce(input, state => {
        state.fromMillisUTC = new Date().getTime();
      });

    case GAME_VICTORY:
      return produce(input, state => {
        state.toMillisUTC = new Date().getTime();
      });

    case GAME_TICK:
      return produce(input, state => {
        let D = Math.floor(GAME_DAYS_PER_SECOND *
          (!!state.toMillisUTC ?
              state.toMillisUTC - state.fromMillisUTC :
              new Date().getTime() - state.fromMillisUTC
          ) / 1000);
        state.daysElapsed = D;

        // If enough days elapsed, process Payroll cycle and record the date
        if (D >= input.cycle.payroll.lastDay + GAME_CYCLE_PAYROLL_DAYS) {
          state.cycle.payroll.lastDay = D;
          state.cycle.payroll.total++;
          // TODO process payroll cycle
          mutPlayerSendMessage(state, 0, PLAYER_MESSAGE_TYPE_PAYROLL, `Did process Payroll cycle #${state.cycle.payroll.total}`);
        }

        // If enough days elapsed, process Expense cycle and record the date
        if (D >= input.cycle.expense.lastDay + GAME_CYCLE_EXPENSE_DAYS) {
          state.cycle.expense.lastDay = D;
          state.cycle.expense.total++;
          // TODO process expense cycle
          mutPlayerSendMessage(state, 0, PLAYER_MESSAGE_TYPE_EXPENSE, `Did process Expense cycle #${state.cycle.expense.total}`);
        }

        // If enough days elapsed, process Market cycle and record the date
        if (D >= input.cycle.market.lastDay + GAME_CYCLE_MARKET_DAYS) {
          state.cycle.market.lastDay = D;
          state.cycle.market.total++;
          // TODO process market cycle
          mutPlayerSendMessage(state, 0, PLAYER_MESSAGE_TYPE_MARKET, `Did process Market cycle #${state.cycle.market.total}`);
        }
      });

    case DEPARTMENT_CLOSE:
      return produce(input, state => {
        // TODO make modifications to state (thanks to Immer)
      });

    case DEPARTMENT_CLOSING_DONE:
      return produce(input, state => {
        // TODO make modifications to state (thanks to Immer)
      });

    case DEPARTMENT_OPEN:
      return produce(input, state => {
        const type = action.departmentType;
        const dept = DEPARTMENTS[type];

        // only if we can afford it
        if (state.player[0].resources.cash >= dept.openingCost) {
          state.player[0].resources.cash -= dept.openingCost;
          let id = nextUniqueId();
          state.player[0].departmentMap[id] = {
            type,
            name: dept.name,
            state: DEPARTMENT_STATE_OPENING,
            openAtDay: state.daysElapsed + dept.daysToOpen,
          }
          toastr.info(`${dept.name} will be open in ${dept.daysToOpen} days`);

        } else {
          toastr.error(`Costs ${
            MONEY.format(dept.openingCost - state.player[0].resources.cash)
          } more than you have!`);
        }
      });

    case DEPARTMENT_OPENING_DONE:
      return produce(input, state => {
        // TODO make modifications to state (thanks to Immer)
      });

    case PERSON_HIRE:
      return produce(input, state => {
        // TODO make modifications to state (thanks to Immer)
      });

    case PERSON_HIRING_DONE:
      return produce(input, state => {
        // TODO make modifications to state (thanks to Immer)
      });

    case PERSON_TERMINATE:
      return produce(input, state => {
        // TODO make modifications to state (thanks to Immer)
      });

    case PERSON_TERMINATION_DONE:
      return produce(input, state => {
        // TODO make modifications to state (thanks to Immer)
      });

    default:
      return !!input ? input : initialState;
  }
};

/**
 * Player UI: Begin opening <Department Type>; done on day #
 * @param departmentType
 * @returns {{type: string, departmentType: *}}
 */
export const doDepartmentOpen = function (departmentType) {
  return {
    type: DEPARTMENT_OPEN,
    departmentType
  }
}

/**
 * Player UI: <Department ID> opened
 * @param departmentId
 * @returns {{departmentId: *, type: string}}
 */
export const doDepartmentOpeningDone = function (departmentId) {
  return {
    type: DEPARTMENT_OPENING_DONE,
    departmentId
  }
}

/**
 * Player UI: Begin closing <Department ID>; done on day #
 * @param departmentId
 * @returns {{departmentId: *, type: string}}
 */
export const doDepartmentClose = function (departmentId) {
  return {
    type: DEPARTMENT_CLOSE,
    departmentId
  }
}

/**
 * Player UI: <Department ID> closed
 * @param departmentId
 * @returns {{departmentId: *, type: string}}
 */
export const doDepartmentClosingDone = function (departmentId) {
  return {
    type: DEPARTMENT_CLOSING_DONE,
    departmentId
  }
}

/**
 * Player UI: Begin hiring <Person Type>; done on day #
 * @param personType
 * @returns {{type: string, personType: *}}
 */
export const doPersonHire = function (personType) {
  return {
    type: PERSON_HIRE,
    personType
  }
}

/**
 * Player UI: <Person ID> hired
 * @param personId
 * @returns {{personId: *, type: string}}
 */
export const doPersonHiringDone = function (personId) {
  return {
    type: PERSON_HIRING_DONE,
    personId
  }
}

/**
 * Player UI: Begin terminating <Person ID>; done on day #
 * @param personId
 * @returns {{personId: *, type: string}}
 */
export const doPersonTerminate = function (personId) {
  return {
    type: PERSON_TERMINATE,
    personId
  }
}

/**
 * Player UI: <Person ID> terminated
 * @param personId
 * @returns {{personId: *, type: string}}
 */
export const doPersonTerminationDone = function (personId) {
  return {
    type: PERSON_TERMINATION_DONE,
    personId
  }
}

/**
 Get the game status
 */
export const selectDaysElapsed = () => {
  return createSelector(
    [(state) => {
      return state.board.daysElapsed;
    }],
    (value) => {
      return value;
    });
};

/**
 Get the # of days until we will process the next cycle for Payroll, Expense, and Market

 @return {OutputSelector<unknown, {market, payroll, expense}, (res: {market: number, payroll: number, expense: number}) => {market, payroll, expense}>}
 */
export const selectDaysUntilNextCycle = () => {
  return createSelector(
    [(state) => {
      return {
        payroll: Math.floor(GAME_CYCLE_PAYROLL_DAYS - state.board.daysElapsed % GAME_CYCLE_PAYROLL_DAYS),
        expense: Math.floor(GAME_CYCLE_EXPENSE_DAYS - state.board.daysElapsed % GAME_CYCLE_EXPENSE_DAYS),
        market: Math.floor(GAME_CYCLE_MARKET_DAYS - state.board.daysElapsed % GAME_CYCLE_MARKET_DAYS),
      };
    }],
    (value) => {
      return value;
    });
};

/**
 Get the # of days in each cycle for Payroll, Expense, and Market

 @return {OutputSelector<unknown, {market, payroll, expense}, (res: {market: number, payroll: number, expense: number}) => {market, payroll, expense}>}
 */
export const selectTotalDaysInCycle = () => {
  return createSelector(
    [(state) => {
      return {
        payroll: GAME_CYCLE_PAYROLL_DAYS,
        expense: GAME_CYCLE_EXPENSE_DAYS,
        market: GAME_CYCLE_MARKET_DAYS,
      };
    }],
    (value) => {
      return value;
    });
};

/**
 Get the total # of cycles that we have processed for Payroll, Expense, and Market

 @return {OutputSelector<unknown, {market, payroll, expense}, (res: {market: number, payroll: number, expense: number}) => {market, payroll, expense}>}
 */
export const selectTotalCycles = () => {
  return createSelector(
    [(state) => {
      return {
        payroll: state.board.cycle.payroll.total,
        expense: state.board.cycle.expense.total,
        market: state.board.cycle.market.total,
      };
    }],
    (value) => {
      return value;
    });
};

/**
 Get the % of market share uncaptured by any player

 @return {OutputSelector<unknown, {number}, (res: {number}) => {number}>}
 */
export const selectUncapturedMarketShare = () => {
  return createSelector(
    [(state) => {
      return state.board.uncapturedMarketShare;
    }],
    (value) => {
      return value;
    });
};

/**
 Get the entire slice of board store for a given player
 <p>
 NOTE: `departmentIdsByType` is automatically computed when selecting-- this data is never written

 @param id which player (numbered starting at 0) you want the data for
 @return {OutputSelector<unknown, {*}, (res: {*}) => {*}>}
 */
export const selectPlayerData = (id) => {
  return createSelector(
    [(state) => {
      return state.board.player[id];
    }],
    (value) => {
      let departmentIdsByType = {};
      for (let type in DEPARTMENTS)
        departmentIdsByType[type] = [];
      for (let id in value.departmentMap)
        if (value.departmentMap.hasOwnProperty(id))
          departmentIdsByType[value.departmentMap[id].type].push(id)
      return {...value, departmentIdsByType};
    });
};
